/**
 * Node factories + tooltip phrasing for the FO Rules authoring row.
 *
 * Selecting a picker option builds the matching discriminated {@link RuleNode}
 * (from the shared foundation); switching op/scope/percent stays inside the
 * ranking node. All helpers are pure so the row stays a thin view.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowLayer, FlowTagRankingOp, RuleNode } from '@trafficpulse/shared';

import { OP_META } from '../OperatorPills';
import {
  rankingMetricDescription,
  rankingMetricLabel,
  rankingNeedsPercent,
  type ConditionNode,
  type PickerSelection,
} from './condition-kind';

type RankingNode = Extract<RuleNode, { kind: 'ranking' }>;
type TagNode = Extract<RuleNode, { kind: 'tag' }>;
type MetricNode = Extract<RuleNode, { kind: 'condition' }>;

/** Default percent seeded for top/bottom-% ranking ops (matches the tag page). */
export const DEFAULT_RANKING_PERCENT = 10;
const DEFAULT_RANKING_OP: FlowTagRankingOp = 'highest';

/** Build the node for a freshly picked field, preserving same-kind settings. */
export function nodeFromSelection(
  sel: PickerSelection,
  prev: ConditionNode,
  layer: FlowLayer,
): RuleNode {
  if (sel.kind === 'ranking') return makeRankingNode(sel.metric, prev, layer);
  if (sel.kind === 'tag') return makeTagNode(sel.tagName, prev);
  return makeMetricNode(sel.metric, prev);
}

function makeRankingNode(
  metric: RankingNode['metric'],
  prev: ConditionNode,
  layer: FlowLayer,
): RankingNode {
  const prior = prev.kind === 'ranking' ? prev : undefined;
  const op = prior?.op ?? DEFAULT_RANKING_OP;
  const node: RankingNode = { kind: 'ranking', metric, op };
  if (rankingNeedsPercent(op)) node.percent = prior?.percent ?? DEFAULT_RANKING_PERCENT;
  // Paths always rank flow-wide, so scope only applies to landers/offers.
  if (layer !== 'paths') node.scope = prior?.scope ?? 'path';
  return node;
}

function makeTagNode(tagName: string, prev: ConditionNode): TagNode {
  return { kind: 'tag', tagName, negate: prev.kind === 'tag' ? (prev.negate ?? false) : false };
}

function makeMetricNode(metric: string, prev: ConditionNode): MetricNode {
  const prior = prev.kind === 'condition' ? prev : undefined;
  return {
    kind: 'condition',
    metric,
    op: prior?.op ?? '<',
    value: prior?.value ?? 0,
    // A metric→metric switch keeps the comparison; a kind change drops it.
    ...(prior?.compareTo ? { compareTo: prior.compareTo } : {}),
  };
}

/** Change the ranking op, adding/dropping `percent` to match the new op. */
export function setRankingOp(node: RankingNode, op: FlowTagRankingOp): RankingNode {
  if (rankingNeedsPercent(op)) {
    return { ...node, op, percent: node.percent ?? DEFAULT_RANKING_PERCENT };
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { percent, ...rest } = node;
  return { ...rest, op };
}

// --- Tooltip: one natural sentence per kind --------------------------------

export function conditionTooltip(
  node: ConditionNode,
  metricLabel: (id: string) => string,
): string {
  if (node.kind === 'ranking') return rankingTooltip(node);
  if (node.kind === 'tag') return tagTooltip(node);
  return metricTooltip(node, metricLabel);
}

function rankingTooltip(node: RankingNode): string {
  const label = rankingMetricLabel(node.metric);
  const pct = node.percent ?? DEFAULT_RANKING_PERCENT;
  const where =
    node.op === 'highest'
      ? `${label} is the highest`
      : node.op === 'lowest'
        ? `${label} is the lowest`
        : node.op === 'top_percent'
          ? `${label} is in the top ${pct}%`
          : `${label} is in the bottom ${pct}%`;
  return `Items whose ${where}${scopeClause(node)} keep their current weight. ${rankingMetricDescription(node.metric)}`;
}

function scopeClause(node: RankingNode): string {
  if (!node.scope) return '';
  return node.scope === 'flow' ? ' across the flow' : ' within its path';
}

function tagTooltip(node: TagNode): string {
  const verb = node.negate ? 'not tagged' : 'tagged';
  return `Items ${verb} ${node.tagName} keep their current weight.`;
}

function metricTooltip(node: MetricNode, metricLabel: (id: string) => string): string {
  const friendly = metricLabel(node.metric);
  const phrase = OP_META[node.op]?.phrase ?? `is ${node.op}`;
  const ct = node.compareTo;
  if (ct) {
    const win = ct.dateRange ? ` over ${ct.dateRange}` : ' over the same window';
    const pct = ct.percent === undefined ? '' : `${ct.percent}% of `;
    const src = ct.against === 'total' ? 'TOTAL ' : '';
    const rhs = `${pct}${src}${metricLabel(ct.metric)}${win}`;
    return `Items where ${friendly} ${phrase} ${rhs} keep their current weight. No data in the compare window ⇒ the condition never matches.`;
  }
  return `Items where ${friendly} ${phrase} ${node.value} keep their current weight.`;
}
